import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Dropdown } from 'basic-components';
import { DataStorageQuery, HooksService, Query } from 'dashboard-services';

import { creatorCurvesActions } from 'actions/curves';
import getApiConfig from 'api/ApiConfig';
import properties from 'resources/constants/properties.json';
import { CurvesUtils } from 'utils';

import { useQueries } from '@tanstack/react-query';

import './Columns.scss';

const useGetColumns = ({ selectedCurves, getQueryBuilder, source }) => {
  const queries = useMemo(() => 
    selectedCurves.map((curve, curveIndex) => ({
      ...DataStorageQuery.QUERIES.getSymbolColumns(getQueryBuilder, {
        groupName: curve.groupName,
        query: undefined,
        metadatas: { [CurvesUtils.ROOT]: curve.root, [CurvesUtils.PRODUCT]: curve.product },
        symbols: { [CurvesUtils.SYMBOL]: `${curve.root}_*` },
        size: 50,
        useEs: properties.useEsSymbolListing === "true",
        curveIndex,
        provider: source
      }),
      select: response => (response?.items || []).map(item => item?.name)
    })), [getQueryBuilder, selectedCurves, source]),
  combine = useCallback(results => ({
    data: results.map(result => result?.data).flat().toUnique().filter(v => !!v).sort((a, b) => a.localeCompare(b)).map(v => ({ label: v, value: v })),
    isPending: results.some(result => result.isPending)
  }), [])

  return useQueries({
    queries,
    combine
  })
}

const emptyArray = []
const Columns = () => {
  const dispatch = useDispatch(),
        selectedCurves = useSelector(state => state.curvesState.creatorState.selectedCurves) || emptyArray,
        selectedColumns = useSelector(state => state.curvesState.creatorState.selectedColumns) || undefined,
        source = useSelector(state => state.globalState.source),
        calls = useRef([]),
        onChange = useCallback((value, { name }) => dispatch(creatorCurvesActions.onChange(value, name)), [dispatch]),
        getQueryBuilder = useCallback(() => 
          new Query.Builder(dispatch(getApiConfig()))
            .withFetching(false)
            .withHeader("Target", source, true)
        , [dispatch, source]),
        { data: columns = emptyArray } = useGetColumns({ getQueryBuilder, selectedCurves, source }),
        prevColumns = HooksService.usePrevious(columns),
        prevSelectedColumns = HooksService.usePrevious(selectedColumns),
        columnsRef = useRef()

  useEffect(() => {
    if((!prevColumns || prevColumns?.length === 0) && columns[0] && (!selectedColumns || selectedColumns?.length === 0) && (!prevSelectedColumns || prevSelectedColumns?.length === 0)) {
      columnsRef.current?.dropdown.current?.setValue(columns[0])
    }
  }, [columns, onChange, prevColumns, prevSelectedColumns, selectedColumns])

  useEffect(() => () => {
    calls.current?.forEach(c => c?.cancel?.())
  }, [])

  return (
    <div className="ng-office-app__authed__content__basket__body__data__columns">
      <span className="ng-office-app__authed__content__basket__body__data__columns__label">Column</span>
      <div className="ng-office-app__authed__content__basket__body__data__columns__dropdown">
        <Dropdown
            creatable
            defaultValue={selectedColumns}
            disabled={selectedCurves.length === 0}
            fixed
            menuPlacement="bottom"
            menuPortalTarget={document.querySelector('.ng-office-app')}
            name="selectedColumns"
            noError
            onChange={onChange}
            options={columns}
            placeholder="Columns"
            ref={columnsRef}
            variant="border"
        />
      </div>
    </div>
  )
}

export default Columns;